/*----------------------------------
- LANDING EXPERIENCE: HOME
----------------------------------*/

/**
 * Home on the design system's marketing layer (docs/mockups/website-home-v3.html, batch 7 C4): the thin composer of the
 * frame (the pill header, the world footer with Home's closing) and the sections, each its own component under
 * `components/home/`. It owns what the sections share: the page's one persona, the arrivals switch and the hero picture
 * the floating dock chose (`useHomeHero`).
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0028
 * @adr  ADR-0110
 * @rule The root carries `ud-home`: every rule of `marketing/styles/home.css` that names a shared part is scoped to it,
 *       because a page chunk's sheet stays loaded after a client navigation.
 * @rule The Home rows are the ten of the bare `/domains` search (ADR-0028), in tab 1 of the demo; an unready search
 *       shows an empty state, never fallback rows.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useEffect, useMemo, useRef } from 'preact/hooks';

import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import HomeAskAi from '@/client/pages/landing/components/home/HomeAskAi';
import HomeClosingCard from '@/client/pages/landing/components/home/HomeClosingCard';
import HomeFaq from '@/client/pages/landing/components/home/HomeFaq';
import HomeFeatureCards from '@/client/pages/landing/components/home/HomeFeatureCards';
import HomeFunnel from '@/client/pages/landing/components/home/HomeFunnel';
import HomeHeroDock from '@/client/pages/landing/components/home/HomeHeroDock';
import HomeStage from '@/client/pages/landing/components/home/HomeStage';
import { useHomeHero } from '@/client/pages/landing/components/home/useHomeHero';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildHomeGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { startHomeArrivals } from '@/client/pages/landing/lib/homeArrivals';
import { useHomePersona } from '@/client/pages/landing/lib/useHomePersona';
import type { InitialData } from '@/client/pages/landing/types';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';

/*----------------------------------
- TYPES
----------------------------------*/

type LandingExperienceProps = {
    initialData: InitialData;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'landing_home';

/*----------------------------------
- COMPONENT
----------------------------------*/

const LandingExperience = ({ initialData }: LandingExperienceProps) => {
    const { stats, links, navigation, domainsPreview } = initialData;
    const rootRef = useRef<HTMLDivElement>(null);
    const [persona, setPersona] = useHomePersona();
    const [hero, setHero] = useHomeHero();
    const homeGeoContext = useMemo(() => buildHomeGeoContext(), []);

    // `html.js` and the arrivals, from a `.ts` helper that marks what is already on screen first (homeArrivals.ts).
    useEffect(() => (rootRef.current ? startHomeArrivals(rootRef.current) : undefined), []);

    return (
        <div class="ud-home" ref={rootRef}>
            {/* The hero card sits under the floating pill (`--card-top`), so Home reserves no spacer. */}
            <Header links={links} navigation={navigation} currentPath={LANDING_WEBSITE_PAGE_ROUTES.home} trackingPage={TRACKING_PAGE} spacer={false} />

            <main>
                <HomeStage
                    totalSignups={stats.totalSignups}
                    totalDomains={stats.totalDomains}
                    preview={domainsPreview}
                    persona={persona}
                    hero={hero}
                    onPersonaChange={setPersona}
                />
                <HomeFunnel stats={stats} />
                <HomeFeatureCards ctaHref={links.productBaseUrl} />
                <HomeClosingCard />
                <HomeFaq />
                <HomeAskAi geoContext={homeGeoContext} />
                <PageGeoBlock geo={homeGeoContext} surface={TRACKING_PAGE} />
            </main>

            <Footer links={links} trackingPage={TRACKING_PAGE} stats={stats} homeClosing />

            {/* Last, outside the stage, as the mock draws it: the stage isolates its stacking context, so a fixed dock
                inside it was painted over by the sticky cards, the closing card and the footer scrolling past it. */}
            <HomeHeroDock hero={hero} onChange={setHero} />
        </div>
    );
};

export default LandingExperience;
